import { Button } from 'primereact/button';
import { Dispatch, FC, MutableRefObject, SetStateAction, useState } from 'react';
import refreshTokenOrLogout from '../../utils/refreshTokenOrLogout';
import { isTokenExpired } from '../../utils/isTokenExpired';
import { getAssessmentStatus } from '../../bffApiCalls/getAssessmentStatus';
import { useAuth0 } from '@auth0/auth0-react';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import { ItemsAppInterface } from '../../vite-env';
import { AttemptInformation, attemptInfoFromScores } from '../../models/AttemptInformation';
import { usePostHog } from '@posthog/react';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { LearnosityScores } from '../../models/LearnosityScores';
import { submitAssessmentAsync } from '../../utils/submitAssessmentAsync';
import { captureLearnosityException } from '../../utils/learnositySentry';

interface Props {
  setConfirmationModalState: Dispatch<SetStateAction<boolean>>;
  incompleteQuestions: number;
  request: LearnosityRequest;
  setAlreadySubmitted: Dispatch<SetStateAction<boolean>>;
  setAssessmentInProgress: Dispatch<SetStateAction<boolean>>;
  itemsApp: MutableRefObject<ItemsAppInterface | null>;
  setAttemptInfo?: (attempt: AttemptInformation) => void;
  setScores: Dispatch<SetStateAction<LearnosityScores | null>>;
  setSubmitComplete: Dispatch<SetStateAction<boolean>>;
  mathRecoverySaving?: boolean;
}

function getScoresAsync(itemsApp: ItemsAppInterface): Promise<LearnosityScores> {
  return new Promise(resolve => {
    // Resolves issue with some scores being null. May not be required in the
    // future once Learnosity fixes itemsApp.getScores(). See TEAMF-609 for context.
    itemsApp.questionsApp().getScores(resolve);
  });
}

export const SubmitConfirmationModalContent: FC<Props> = ({
  setConfirmationModalState,
  incompleteQuestions,
  request,
  setAlreadySubmitted,
  itemsApp,
  setAssessmentInProgress,
  setAttemptInfo,
  setScores,
  setSubmitComplete,
  mathRecoverySaving = false,
}) => {
  const previewMode = usePreviewMode();
  const [loading, setLoading] = useState(false);
  const { getAccessTokenSilently, logout } = useAuth0();
  const posthog = usePostHog();
  const checkTokenRefresh = async () => {
    if (isTokenExpired()) {
      await refreshTokenOrLogout(getAccessTokenSilently, logout, posthog);
    }
  };
  const onSubmit = async () => {
    if (previewMode) {
      let scores = {};
      if (itemsApp.current) {
        scores = await getScoresAsync(itemsApp.current);
      }
      setAttemptInfo?.(attemptInfoFromScores(scores));
      setAssessmentInProgress(false);
      setConfirmationModalState(false);
      return;
    }
    setLoading(true);
    try {
      await checkTokenRefresh();
      const response = await getAssessmentStatus(
        request.request.user_id,
        request.request.session_id,
        getAccessTokenSilently,
        logout,
        posthog,
      );
      if (response?.status === 'Completed') {
        setAssessmentInProgress(false);
        setLoading(false);
        setConfirmationModalState(false);
        return setAlreadySubmitted(true);
      }
      if (itemsApp.current) {
        const scores = await getScoresAsync(itemsApp.current);
        await submitAssessmentAsync(itemsApp.current, {
          show_submit_confirmation: false,
          show_submit_ui: false,
        });
        // we need to ensure that the submit process completes
        // before we take post submit actions. A separate bit of state
        // is used in an attempt to make that more visible in the code.
        setSubmitComplete(true);
        setScores(scores);
      }
    } catch (e) {
      // Token refresh or SDK score/submission work can fail here. Release the
      // loading state so the submit button doesn't stay stuck spinning.
      setLoading(false);
      captureLearnosityException(e, {
        api: 'items',
        operation: 'review_submit',
        assessmentType: 'skill',
        activityReference: request.request.activity_id,
        renderingType: request.request.rendering_type,
        assessmentState: request.request.state,
        learnerId: request.request.user_id,
        sessionId: request.request.session_id,
      });
    }
  };

  return (
    <div className="tw-p-2">
      <div className="tw-text-center">
        <h3>{incompleteQuestions === 0 ? 'Almost done!' : `You have ${incompleteQuestions} unanswered questions`}</h3>
        <p className="tw-font-semibold">
          {incompleteQuestions === 0
            ? 'Are you sure you want to submit?'
            : 'Want to keep working on them, or submit as is?'}
        </p>
        <p> {incompleteQuestions === 0 ? `You won't be able to change your answers after this.` : ''}</p>
      </div>
      <div className="tw-flex tw-justify-end tw-mt-8">
        <Button
          label={incompleteQuestions === 0 ? 'Cancel' : 'Keep working'}
          outlined
          className="tw-mr-2"
          onClick={() => setConfirmationModalState(false)}
        />
        <Button
          loading={loading}
          disabled={loading || mathRecoverySaving}
          label={incompleteQuestions === 0 ? 'Submit' : 'Submit anyway'}
          onClick={onSubmit}
        />
      </div>
    </div>
  );
};
